import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Import the cyberfunk logo
import cyberfunkLogo from '../assets/cyberfunk_logo.png';

// Styled components
const PodcastIconContainer = styled(Box)(({ theme, showTooltip }) => ({
  position: 'fixed',
  bottom: '100px',
  right: '20px',
  zIndex: showTooltip ? 9990 : 9998, // Lower z-index when tooltip is shown
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
}));

const PodcastIconOverlay = styled(Box)(({ theme }) => ({
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease-in-out',
  boxShadow: '0 4px 20px rgba(33, 150, 243, 0.3)',
  border: '2px solid rgba(255, 255, 255, 0.2)',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 6px 25px rgba(33, 150, 243, 0.5)',
  },
}));

const PodcastTooltip = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: '10px',
  right: '80px',
  width: '280px',
  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
  border: '2px solid #2196f3',
  borderRadius: '12px',
  padding: theme.spacing(2),
  zIndex: 10013,
  boxShadow: '0 12px 40px rgba(33, 150, 243, 0.4)',
  backdropFilter: 'blur(10px)',
  animation: 'tooltipSlideIn 0.3s ease-out',
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: '20px',
    right: '-8px',
    width: 0,
    height: 0,
    borderTop: '8px solid transparent',
    borderBottom: '8px solid transparent',
    borderLeft: '8px solid #1976d2', // Blue arrow pointing right
  },
  '@keyframes tooltipSlideIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(10px) scale(0.95)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0) scale(1)',
    },
  },
}));

const PodcastIcon = () => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [showHoverTooltip, setShowHoverTooltip] = useState(false);

  const handleIconClick = () => {
    setShowTooltip(!showTooltip);
  };

  const handleListenNow = () => {
    window.open('https://cyberlagebild.de/podcast', '_blank');
    setShowTooltip(false);
  };

  const handleLater = () => {
    setShowTooltip(false);
  };

  return (
    <PodcastIconContainer showTooltip={showTooltip || showHoverTooltip}>
      {/* Podcast Icon */}
      <PodcastIconOverlay
        onClick={handleIconClick}
        onMouseEnter={() => setShowHoverTooltip(true)}
        onMouseLeave={() => setShowHoverTooltip(false)}
        title="Cyberlagebild Podcast"
      >
        <img
          src={cyberfunkLogo}
          alt="Cyberfunk Podcast"
          style={{
            width: '60px',
            height: '60px',
            objectFit: 'contain',
          }}
        />
      </PodcastIconOverlay>
      
      {showHoverTooltip && (
        <PodcastTooltip>
          <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
            Cyberfunk Podcast
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            Stimmen aus dem Lagezentrum
          </Typography>
        </PodcastTooltip>
      )}

      {/* Podcast Tooltip */}
      {showTooltip && (
        <PodcastTooltip>
          <Typography
            variant="h6"
            sx={{
              color: '#ffffff',
              fontWeight: 'bold',
              marginBottom: 1,
              textAlign: 'center',
            }}
          >
            Cyberfunk zum Mittag - Wissen das schmeckt
          </Typography>
          
          <Typography
            variant="body2"
            sx={{
              color: '#e3f2fd',
              marginBottom: 2,
              textAlign: 'center',
              fontStyle: 'italic',
            }}
          >
            Alle zwei Wochen montags um 12:00 Uhr eine neue Folge unseres Podcasts:
          </Typography>
          
          <Typography
            variant="body2"
            sx={{
              color: '#bbdefb',
              marginBottom: 2,
              textAlign: 'center',
              fontSize: '0.85rem',
            }}
          >
            "Cyberfunk - Stimmen aus dem Lagezentrum"
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleListenNow}
              sx={{
                background: 'linear-gradient(135deg, #42a5f5 0%, #2196f3 100%)',
                color: '#ffffff',
                fontWeight: 'bold',
                padding: '6px 16px',
                borderRadius: '6px',
                fontSize: '0.8rem',
                '&:hover': {
                  background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              Jetzt anhören
            </Button>
            
            <Button
              variant="outlined"
              size="small"
              onClick={handleLater}
              sx={{
                color: '#ffffff',
                borderColor: '#ffffff',
                padding: '6px 16px',
                borderRadius: '6px',
                fontSize: '0.8rem',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderColor: '#e3f2fd',
                },
              }}
            >
              Später
            </Button>
          </Box>
        </PodcastTooltip>
      )}
    </PodcastIconContainer>
  );
};

export default PodcastIcon;
